import { useState } from "react";

const travelPatterns = [
  {
    icon: "ri-ship-line",
    title: "Cruises from Port Tampa Bay",
    description:
      "St. Petersburg cruisers frequently sail from Port Tampa Bay (~25 mi via I-275) to the Caribbean, Mexico, and Central America. Closed-loop cruises technically allow a birth certificate + photo ID, but a full passport book is strongly recommended for international departures and emergency re-entry.",
    timeline: "Apply 6–12 weeks before sailing",
    urgencyNote: "Booked a cruise in the next 3 weeks? You need expedited processing.",
  },
  {
    icon: "ri-palette-line",
    title: "Art & Cultural Tourism (Dali Museum)",
    description:
      "St. Petersburg is home to the world-renowned Dali Museum, attracting art enthusiasts who travel internationally for museum exhibitions, gallery tours in Europe, and cultural immersion. A valid passport is your ticket to those bucket-list art destinations.",
    timeline: "Apply 8–12 weeks before departure",
    urgencyNote: "European museum season starts soon? Don't wait — expedite now.",
  },
  {
    icon: "ri-trophy-line",
    title: "Spring Training & Baseball Travel",
    description:
      "Rays fans travel to spring training in Port Charlotte and follow the team on road trips to Toronto and Latin America. Many St. Petersburg baseball travelers also book Caribbean cruises and Mexico getaways during spring break season.",
    timeline: "Apply 6–10 weeks before spring training trips",
    urgencyNote: "Spring training or road trip in 2–4 weeks? You'll need expedited service.",
  },
  {
    icon: "ri-sun-line",
    title: "Beach Getaways & Island Hopping",
    description:
      "St. Pete Beach, Pass-a-Grille, and Treasure Island locals love quick Caribbean escapes to the Bahamas, Jamaica, and Aruba. These trips are often booked spontaneously — and nothing kills a last-minute island escape faster than an expired passport.",
    timeline: "Apply 6–8 weeks before island trips",
    urgencyNote: "Island trip in 2–3 weeks? Expedited processing is your only safe bet.",
  },
];

export default function StPetersburgTravelTimingSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="travel-timing" className="bg-white py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Plan Ahead</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            When St. Petersburg Travelers Need Their Passports
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            From cruise departures at Port Tampa Bay to Dali Museum art tours in Europe and spring break getaways, here are the most common travel patterns for Pinellas County residents — and when you should start your passport application.
          </p>
        </div>

        <div className="space-y-4">
          {travelPatterns.map((pattern, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-start gap-4 p-5 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] flex-shrink-0 mt-0.5">
                  <i className={`${pattern.icon} text-[#F6C344] text-lg`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-bold text-[#1A365D] text-sm leading-snug">{pattern.title}</h3>
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A365D] flex-shrink-0">
                      <i
                        className={`text-white text-sm transition-transform duration-200 ${
                          openIndex === i ? "ri-subtract-line" : "ri-add-line"
                        }`}
                      ></i>
                    </div>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">{pattern.timeline}</p>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5 pt-1">
                  <div className="border-t border-gray-200 pt-4 ml-14">
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">{pattern.description}</p>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p className="text-amber-800 text-xs font-semibold leading-relaxed">
                        <i className="ri-alarm-warning-line text-amber-600 mr-1.5"></i>
                        {pattern.urgencyNote}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 bg-[#1A365D] rounded-lg p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
              <i className="ri-calendar-check-line text-[#1A365D] text-lg"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm mb-1">Not sure when to apply?</p>
              <p className="text-white/70 text-xs leading-relaxed">
                A good rule of thumb: if your trip is within 10 weeks, standard government processing may not make it in time. Start your expedited order now and travel with confidence.
              </p>
            </div>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Check My Timeline <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}